import type { Traveler } from "@b2bportal/traveler-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { specialCharacterRegex } from "@hopper-b2b/utilities";
import dayjs from "dayjs";

export enum PassportFormFields {
  Nationality = "nationality",
  PassportNumber = "passportNumber",
  PassportExpiryDate = "passportExpiryDate",
  PassportIssuanceCountry = "passportIssuanceCountry",
}

export type PassengerFormContext = {
  [PassportFormFields.Nationality]: string;
  [PassportFormFields.PassportNumber]: string;
  [PassportFormFields.PassportExpiryDate]: string;
  [PassportFormFields.PassportIssuanceCountry]: string;
};

export const usePassportFormFields = ({
  dateFormat = "DD/MM/YYYY",
  traveler,
  requirePassport = false,
}: {
  dateFormat?: string;
  traveler?: Traveler;
  requirePassport?: boolean;
}) => {
  const { t, brand } = useI18nContext();

  const passportDetailsDefaultValues: PassengerFormContext = {
    [PassportFormFields.Nationality]: brand.preferredCountryCode,
    [PassportFormFields.PassportNumber]: "",
    [PassportFormFields.PassportExpiryDate]: "",
    [PassportFormFields.PassportIssuanceCountry]: brand.preferredCountryCode,
  };

  const formFields = {
    [PassportFormFields.Nationality]: requirePassport
      ? { required: t("travelerForm.errors.nationalityRequired") }
      : {},
    [PassportFormFields.PassportNumber]: {
      ...(requirePassport && {
        required: t("travelerForm.errors.passportRequired"),
      }),
      validate: (passportNumber: string) => {
        if (!passportNumber) return undefined;

        if (/\s/.test(passportNumber)) {
          return t("noWhitespaceError");
        }
        if (
          traveler?.airTravelerDetails?.passport?.number !== passportNumber &&
          !specialCharacterRegex.test(passportNumber)
        ) {
          return t("noSpecialCharactersError");
        }
      },
    },
    [PassportFormFields.PassportExpiryDate]: {
      ...(requirePassport && {
        required: t("travelerForm.errors.passportExpirationRequired"),
      }),
      validate: (
        passportExpiryDate: string,
        formValues: Record<string, string>
      ) => {
        if (!passportExpiryDate) {
          if (formValues?.[PassportFormFields.PassportNumber]) {
            return t("enterAValidExpiry");
          }
          return undefined;
        }

        const dateDayjs = dayjs(passportExpiryDate, dateFormat, true);
        if (!dateDayjs.isValid() || dateDayjs.isBefore(dayjs())) {
          return t("enterAValidExpiry");
        }
      },
    },
    [PassportFormFields.PassportIssuanceCountry]: requirePassport
      ? { required: t("travelerForm.errors.issueCountryRequired") }
      : {},
  };

  return { formFields, passportDetailsDefaultValues };
};
